import type { FinancialMetricMode } from 'partner360/entities/financialMetricMode'
import type { FinData } from 'partner360/entities/pbmDashboard'

import { OVERALL_SUB_CATEGORY } from '../model/constants'
import type { HierarchicalFinData, SobNodeOrderResolver, SobTableEntry } from '../model/types'

import { buildSobPlanningTableModel } from './buildSobPlanningTableModel'

/** Nodes without an order in settings keep the incoming row order at the end of the list */
const UNKNOWN_NODE_ORDER = Number.MAX_SAFE_INTEGER

const sortByNodeOrder = <TItem>(
  items: TItem[],
  getOrder: (item: TItem) => number | undefined
): TItem[] =>
  [...items].sort(
    (left, right) =>
      (getOrder(left) ?? UNKNOWN_NODE_ORDER) - (getOrder(right) ?? UNKNOWN_NODE_ORDER)
  )

const normalizeCategory = (category?: string | null): string => category || OVERALL_SUB_CATEGORY

const isOverallSubCategory = (subCategory?: string | null) =>
  !subCategory || subCategory === OVERALL_SUB_CATEGORY

const normalizeSubCategory = (subCategory?: string | null): string =>
  isOverallSubCategory(subCategory) ? OVERALL_SUB_CATEGORY : (subCategory ?? OVERALL_SUB_CATEGORY)

const isOverallL3Category = (l3Category?: string | null) =>
  !l3Category || l3Category === OVERALL_SUB_CATEGORY

const normalizeL3Category = (l3Category?: string | null): string =>
  isOverallL3Category(l3Category) ? OVERALL_SUB_CATEGORY : (l3Category ?? OVERALL_SUB_CATEGORY)

const buildL3TableEntries = ({
  category,
  subCategory,
  rows,
  currentFiscalYear,
  metricMode,
  prefixKeys,
  getNodeOrder,
}: {
  category: string
  subCategory: string
  rows: HierarchicalFinData[]
  currentFiscalYear: number
  metricMode: FinancialMetricMode
  prefixKeys: boolean
  getNodeOrder?: SobNodeOrderResolver
}): SobTableEntry[] => {
  const l3Rows = rows.filter((row) => !isOverallL3Category(row.L3Category))

  if (!l3Rows.length) {
    return []
  }

  const l3CategoryGroups = l3Rows.reduce<Map<string, HierarchicalFinData[]>>((groups, row) => {
    const l3Category = normalizeL3Category(row.L3Category)
    const currentRows = groups.get(l3Category) ?? []

    currentRows.push(row)
    groups.set(l3Category, currentRows)

    return groups
  }, new Map())

  const orderedL3CategoryGroups = sortByNodeOrder(
    Array.from(l3CategoryGroups.entries()),
    ([l3Category]) => getNodeOrder?.({ category, subCategory, l3Category })
  )

  return orderedL3CategoryGroups.map<SobTableEntry>(([l3Category, groupRows]) => ({
    key: prefixKeys
      ? `${category}::${subCategory}::${l3Category}`
      : `${subCategory}::${l3Category}`,
    title: l3Category,
    category,
    subCategory,
    l3Category,
    rows: groupRows,
    model: buildSobPlanningTableModel({
      rows: groupRows,
      currentFiscalYear,
      metricMode,
    }),
    isOverall: false,
  }))
}

const buildCategoryTableEntries = ({
  category,
  rows,
  currentFiscalYear,
  metricMode,
  prefixKeys,
  getNodeOrder,
}: {
  category: string
  rows: HierarchicalFinData[]
  currentFiscalYear: number
  metricMode: FinancialMetricMode
  prefixKeys: boolean
  getNodeOrder?: SobNodeOrderResolver
}): SobTableEntry[] => {
  const hierarchyRows = rows
  const subCategoryRows = hierarchyRows.filter((row) => !isOverallSubCategory(row.SubCategory))

  if (!subCategoryRows.length) {
    return [
      {
        key: prefixKeys ? `${category}::${OVERALL_SUB_CATEGORY}` : OVERALL_SUB_CATEGORY,
        title: OVERALL_SUB_CATEGORY,
        category,
        subCategory: OVERALL_SUB_CATEGORY,
        l3Category: OVERALL_SUB_CATEGORY,
        rows: hierarchyRows,
        model: buildSobPlanningTableModel({ rows, currentFiscalYear, metricMode }),
        isOverall: true,
      },
    ]
  }

  const subCategoryGroups = subCategoryRows.reduce<Map<string, HierarchicalFinData[]>>(
    (groups, row) => {
      const subCategory = normalizeSubCategory(row.SubCategory)
      const currentRows = groups.get(subCategory) ?? []

      currentRows.push(row)
      groups.set(subCategory, currentRows)

      return groups
    },
    new Map()
  )

  const orderedSubCategoryGroups = sortByNodeOrder(
    Array.from(subCategoryGroups.entries()),
    ([subCategory]) => getNodeOrder?.({ category, subCategory, l3Category: OVERALL_SUB_CATEGORY })
  )

  const subCategoryEntries = orderedSubCategoryGroups.map<SobTableEntry>(
    ([subCategory, groupRows]) => {
      const childEntries = buildL3TableEntries({
        category,
        subCategory,
        rows: groupRows,
        currentFiscalYear,
        metricMode,
        prefixKeys,
        getNodeOrder,
      })
      const l3OverallRows = groupRows.filter((row) => isOverallL3Category(row.L3Category))
      const modelRows = childEntries.length && l3OverallRows.length ? l3OverallRows : groupRows

      return {
        key: prefixKeys ? `${category}::${subCategory}` : subCategory,
        title: subCategory,
        category,
        subCategory,
        l3Category: childEntries.length
          ? OVERALL_SUB_CATEGORY
          : normalizeL3Category(groupRows[0]?.L3Category),
        rows: modelRows,
        model: buildSobPlanningTableModel({
          rows: modelRows,
          currentFiscalYear,
          metricMode,
        }),
        isOverall: false,
        ...(childEntries.length ? { childEntries } : {}),
      }
    }
  )

  const overallRows = hierarchyRows.filter((row) => isOverallSubCategory(row.SubCategory))
  const overallModelRows = overallRows.length ? overallRows : subCategoryRows

  return [
    {
      key: prefixKeys ? `${category}::${OVERALL_SUB_CATEGORY}` : OVERALL_SUB_CATEGORY,
      title: OVERALL_SUB_CATEGORY,
      category,
      subCategory: OVERALL_SUB_CATEGORY,
      l3Category: OVERALL_SUB_CATEGORY,
      rows: overallModelRows,
      model: buildSobPlanningTableModel({
        rows: overallModelRows,
        currentFiscalYear,
        metricMode,
      }),
      isOverall: true,
    },
    ...subCategoryEntries,
  ]
}

export const buildSobTableEntries = ({
  rows,
  currentFiscalYear,
  metricMode,
  getNodeOrder,
}: {
  rows: FinData[]
  currentFiscalYear: number
  metricMode: FinancialMetricMode
  /** Orders categories, subcategories and L3 categories; row order is kept without it */
  getNodeOrder?: SobNodeOrderResolver
}): SobTableEntry[] => {
  const hierarchyRows = rows as HierarchicalFinData[]
  const categoryGroups = hierarchyRows.reduce<Map<string, HierarchicalFinData[]>>((groups, row) => {
    const category = normalizeCategory(row.Category)
    const currentRows = groups.get(category) ?? []

    currentRows.push(row)
    groups.set(category, currentRows)

    return groups
  }, new Map())
  const prefixKeys = categoryGroups.size > 1
  const orderedCategoryGroups = sortByNodeOrder(
    Array.from(categoryGroups.entries()),
    ([category]) =>
      getNodeOrder?.({
        category,
        subCategory: OVERALL_SUB_CATEGORY,
        l3Category: OVERALL_SUB_CATEGORY,
      })
  )

  return orderedCategoryGroups.flatMap(([category, groupRows]) =>
    buildCategoryTableEntries({
      category,
      rows: groupRows,
      currentFiscalYear,
      metricMode,
      prefixKeys,
      getNodeOrder,
    })
  )
}
